/**
 * UI Constants
 * Centralized configuration for colors, spacing, typography, and other UI elements
 */

// Colors
export const COLORS = {
  // Primary
  PRIMARY: '#1ED612',
  PRIMARY_DARK: '#0a0a0a',
  PRIMARY_LIGHT: '#1ED612',
  
  // Background
  BG_DARK: 'rgba(1, 1, 1, 1)',
  BG_CARD: 'rgba(14, 15, 18, 1)',
  BG_HOVER: 'rgba(255, 255, 255, 0.05)',
  BG_GRID: 'rgba(40, 41, 43, 1)',
  
  // Text
  TEXT_PRIMARY: '#FFFFFF',
  TEXT_SECONDARY: '#9CA3AF',
  TEXT_TERTIARY: '#6B7280',
  TEXT_MUTED: '#9CA3AF',
  TEXT_DISABLED: '#6B7280',
  
  // Borders
  BORDER_DEFAULT: 'rgba(40, 41, 43, 1)',
  BORDER_LIGHT: '#333333',
  BORDER_DARK: '#1a1a1a',
  
  // Status
  SUCCESS: '#1ED612',
  WARNING: '#FFA500',
  ERROR: '#FF0000',
  INFO: '#0099FF',
  
  // Rank colors
  RANK_1: '#FFD700', // Gold
  RANK_2: '#C0C0C0', // Silver
  RANK_3: '#CD7F32', // Bronze
  
  // Grayscale
  GRAY_100: '#FFFFFF',
  GRAY_200: '#F3F4F6',
  GRAY_300: '#E5E7EB',
  GRAY_400: '#D1D5DB',
  GRAY_500: '#9CA3AF',
  GRAY_600: '#6B7280',
  GRAY_700: '#374151',
  GRAY_800: '#1F2937',
  GRAY_900: '#111827',
};

// Spacing
export const SPACING = {
  XS: '4px',
  SM: '8px',
  MD: '12px',
  LG: '16px',
  XL: '24px',
  XXL: '32px',
  XXXL: '48px',
};

// Typography
export const TYPOGRAPHY = {
  FONT_FAMILY: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  
  // Font sizes
  SIZE_XS: '10px',
  SIZE_SM: '12px',
  SIZE_BASE: '14px',
  SIZE_LG: '16px',
  SIZE_XL: '20px',
  SIZE_2XL: '24px',
  SIZE_3XL: '32px',
  SIZE_4XL: '40px',
  SIZE_5XL: '48px',
  
  // Font weights
  WEIGHT_NORMAL: 400,
  WEIGHT_MEDIUM: 500,
  WEIGHT_SEMIBOLD: 600,
  WEIGHT_BOLD: 700,
};

// Shadows
export const SHADOWS = {
  NONE: 'none',
  SM: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  MD: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  LG: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  XL: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
};

// Border radius
export const BORDER_RADIUS = {
  NONE: '0px',
  SM: '4px',
  MD: '8px',
  LG: '12px',
  XL: '16px',
  FULL: '9999px',
};

// Animations
export const ANIMATIONS = {
  DURATION_FAST: '150ms',
  DURATION_BASE: '300ms',
  DURATION_SLOW: '500ms',
  
  TIMING_LINEAR: 'linear',
  TIMING_EASE: 'ease',
  TIMING_EASE_IN: 'ease-in',
  TIMING_EASE_OUT: 'ease-out',
  TIMING_EASE_IN_OUT: 'ease-in-out',
};

// Breakpoints
export const BREAKPOINTS = {
  SM: '640px',
  MD: '768px',
  LG: '1024px',
  XL: '1280px',
  XXL: '1536px',
};

// Z-index
export const Z_INDEX = {
  HIDDEN: -1,
  AUTO: 'auto',
  BASE: 0,
  DROPDOWN: 10,
  STICKY: 20,
  FIXED: 30,
  MODAL_BACKDROP: 40,
  MODAL: 50,
  POPOVER: 60,
  TOOLTIP: 70,
};

// Layout
export const LAYOUT = {
  MAX_WIDTH: '90rem',
  CONTAINER_PADDING: '3rem',
  GRID_COLS: 4,
};

// Badge sizes and variants
export const BADGE = {
  SIZES: {
    SM: { padding: '2px 6px', fontSize: '10px' },
    MD: { padding: '4px 8px', fontSize: '12px' },
    LG: { padding: '6px 12px', fontSize: '14px' },
  },
  VARIANTS: {
    PRIMARY: { bg: 'rgba(30, 214, 18, 0.1)', text: '#1ED612', border: '1px solid rgba(30, 214, 18, 0.3)' },
    SECONDARY: { bg: '#1a1a1a', text: '#9CA3AF', border: '1px solid #28292B' },
    SUCCESS: { bg: 'rgba(34, 197, 94, 0.1)', text: '#22C55E', border: '1px solid rgba(34, 197, 94, 0.3)' },
    ERROR: { bg: 'rgba(239, 68, 68, 0.1)', text: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.3)' },
  },
};

// Button styles
export const BUTTON = {
  SIZES: {
    SM: { padding: '6px 12px', fontSize: '12px' },
    MD: { padding: '10px 16px', fontSize: '14px' },
    LG: { padding: '12px 24px', fontSize: '16px' },
  },
  VARIANTS: {
    PRIMARY: { bg: '#1ED612', text: '#000000', hover: 'hover:bg-[#1ED612]/90' },
    SECONDARY: { bg: '#FFFFFF', text: '#000000', hover: 'hover:bg-gray-100' },
    GHOST: { bg: 'transparent', text: '#1ED612', hover: 'hover:bg-[#1ED612]/10' },
  },
};

// Status indicators
export const STATUS = {
  ACTIVE: { color: '#1ED612', label: 'Active' },
  INACTIVE: { color: '#9CA3AF', label: 'Inactive' },
  PENDING: { color: '#FFA500', label: 'Pending' },
  ERROR: { color: '#FF0000', label: 'Error' },
};

// Social links
export const SOCIAL_LINKS = {
  TWITTER: 'https://x.com/xmaquina',
  DISCORD: 'https://discord.com/invite/xmaquina',
  DOCS: 'https://xmaquina.gitbook.io/xmaquina',
};

// Mindshare hero stats
export const HERO_STATS = [
  { id: 1, value: '500,000 DEUS', label: 'Reward Pool', icon: '/dollar-sign.svg', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
  { id: 2, value: '1.8K', label: 'DAO Members', icon: '/users-round.svg', backgroundColor: 'rgba(26, 26, 26, 0.7)' },
  { id: 3, value: '#1', label: 'Active Proposals', icon: '/git-pull-request-create-arrow.svg', backgroundColor: 'rgba(26, 26, 26, 0.7)' },
  { id: 4, value: '4', label: 'Active Quests & Bounties', icon: '/zap.svg', backgroundColor: 'rgba(26, 26, 26, 0.7)' },
];

export default {
  COLORS,
  SPACING,
  TYPOGRAPHY,
  SHADOWS,
  BORDER_RADIUS,
  ANIMATIONS,
  BREAKPOINTS,
  Z_INDEX,
  LAYOUT,
  BADGE,
  BUTTON,
  STATUS,
  SOCIAL_LINKS,
  HERO_STATS,
};
